/**
 * Pricing for one offer module under the new fence pricing structure.
 *
 * The unit price is the country's base price for the module type, plus the model's percentage
 * adjustment. The unit is m² for everything that is built to a size, and a linear metre for a
 * post, which is cut from stock to whatever height the run needs. The
 * per-model prices on ModeleGard are not consulted any more; the small-element thresholds come
 * from the pricing config instead of being hardcoded per module type.
 */

import { moduleCategory } from "./fenceModules";

/**
 * The base price column a module maps to.
 *
 * @param variant  the gate kind, for the two gate families that have priced sub-kinds
 * @param postSize cross-section of a post, in mm
 */
export function priceColumn(tip, variant, postSize) {
	// no configurator module uses a framed panel, so the new structure has no such column and a
	// retired Panou_rama is priced as a panel
	if (tip == "Panou" || tip == "Panou_rama") return "price_panel";

	switch (tip) {
		case "Poarta_mica":
			return "price_gate_pedestrian";
		case "Poarta_batanta":
			return isBifold(variant) ? "price_gate_swing_bifold" : "price_gate_swing";
		case "Poarta_culisanta":
			return slidePriceColumn(variant);
		case "Poarta_autoportanta":
			return "price_gate_selfsupport";
		case "Stalp":
			return POST_PRICE_COLUMNS[postSize] || null;
		// the retired catch-all: every gate kind arrived through the variant
		case "Poarta_mare":
			return legacyGatePriceColumn(variant);
		default:
			return null;
	}
}

const POST_PRICE_COLUMNS = {
	80: "price_post_80",
	100: "price_post_100",
	200: "price_post_200",
};

function isBifold(variant) {
	return variant == "batanta_simpla_bifold" || variant == "batanta_dubla_bifold";
}

function slidePriceColumn(variant) {
	switch (variant) {
		case "culisanta_telescopica":
			return "price_gate_slide_telescopic";
		case "culisanta_articulata":
			return "price_gate_slide_articulated";
		default:
			return "price_gate_slide";
	}
}

function legacyGatePriceColumn(variant) {
	if (isBifold(variant)) return "price_gate_swing_bifold";
	if (variant == "autoportanta") return "price_gate_selfsupport";
	if (variant && variant.indexOf("culisanta") === 0) return slidePriceColumn(variant);
	if (variant == "batanta_simpla" || variant == "batanta_dubla") return "price_gate_swing";

	// the gate kind was never picked
	return null;
}

/**
 * Unit price for a module - per m², or per linear metre for a post: the base price for the
 * country, adjusted for the model.
 *
 * The county margin that used to be applied on top is gone. Where a region costs more to serve,
 * that is carried by its service fees (measure / montage / shipping), not by marking every
 * module up.
 *
 * @param adjustment fraction, 0.2 = +20%
 */
export function moduleUnitPrice({ basePrices, country, tip, variant, postSize, adjustment }) {
	const column = priceColumn(tip, variant, postSize);
	const prices = basePrices && country ? basePrices[String(country).toUpperCase()] : null;

	if (!column || !prices || !prices[column]) return 0;

	return Number(prices[column]) * (1 + (Number(adjustment) || 0));
}

/**
 * The area a module of this type is expected to reach, from the pricing config. Under it a
 * surcharge applies.
 *
 * A driveway gate with an integrated pedestrian gate is judged on its leaf alone, so its
 * threshold is what is left of the driveway threshold once the pedestrian gate is taken out.
 */
export function minAreaFor(minArea, tip, { pedestrianIncluded } = {}) {
	if (!minArea) return 0;

	switch (moduleCategory(tip)) {
		case "panel":
			return Number(minArea.panel) || 0;
		case "pedestrian":
			return Number(minArea.pedestrian) || 0;
		case "driveway":
			return pedestrianIncluded
				? (Number(minArea.driveway) || 0) - (Number(minArea.pedestrian) || 0)
				: Number(minArea.driveway) || 0;
		// a post is priced by the metre, so no area threshold applies
		default:
			return 0;
	}
}

/** Small elements cost proportionally more to make, up to 30% over the threshold price. */
export const MAX_SMALL_ELEMENT_SURCHARGE = 1.3;

/**
 * Multiplier for an element under its threshold: 1 at the threshold, rising as the element gets
 * smaller, capped at MAX_SMALL_ELEMENT_SURCHARGE.
 */
export function smallElementRatio(area, threshold) {
	if (!(threshold > 0) || !(area > 0) || area >= threshold) return 1;

	return Math.min(2 - area / threshold, MAX_SMALL_ELEMENT_SURCHARGE);
}
